import { gettext } from '@eventbrite/i18n';

export const CHANGE_PACKAGE = {
    question: gettext('Can I change my package?'),
    answer: ({ launchNewPackageAndPricing, tld }) => {
        if (launchNewPackageAndPricing) {
            return gettext(
                'Yes! If you’re interested in a custom package, <a href="/contact-sales/">contact our Sales Partnerships Team</a>. Any changes will apply only to events created after you make the change.',
            );
        }

        return gettext(
            'Yes! You can change your package at any time. Upgrades will apply immediately to all your live and drafted events, so you can take advantage of Professional product capabilities.  Downgrades will apply only to events created after you make the change.',
        );
    },
};

export const CHANGE_PACKAGE_AU = {
    question: gettext('Can I change my package?'),
    answer: ({ launchNewPackageAndPricing, tld }) => {
        if (launchNewPackageAndPricing) {
            return gettext(
                'Yes! If you’re interested in a custom package, <a href="/contact-sales/">contact our Sales Partnerships Team</a>. Any changes will apply only to events created after you make the change.',
            );
        }

        return gettext(
            'Yes, you have the freedom to upgrade or downgrade your package at any time. Upgrades will apply instantly to any live and drafted events, allowing you to take advantage of Professional product capabilities straight away. Downgrades will apply only to events that you create after the package change has been made and will not affect existing events.',
        );
    },
};

export const TICKET_TYPE = {
    question: gettext('What is a "ticket type"?'),
    answer: ({ launchNewPackageAndPricing }) =>
        launchNewPackageAndPricing
            ? gettext(
                  'A ticket type is a unique combination of name, price, and availability that you create to build demand among different audiences. For example, an event with "General Admission - Early Bird", "General Admission - Regular", and "VIP" has 3 ticket types.',
              )
            : gettext(
                  'A ticket type is a unique combination of name, price, and availability that you create to create demand or appeal to different audiences. For example, an event with "General Admission - Early Bird", "General Admission - Regular", and "VIP" has 3 ticket types.',
              ),
};

export const TICKET_TYPE_AU = {
    question: gettext('What is a "ticket type"?'),
    answer: ({ launchNewPackageAndPricing }) =>
        launchNewPackageAndPricing
            ? gettext(
                  'A ticket type is a unique combination of name, price, and availability that you create to build demand among different audiences. For example, an event with "General Admission - Early Bird", "General Admission - Regular", and "VIP" has 3 ticket types.',
              )
            : gettext(
                  'If you want to offer a variety of price points, availability, or experiences to drive demand or appeal to different audiences, you will need to create multiple "ticket types". For example, if you offer one ticket option (e.g. General Admission) at a single price point for all attendees, your event has one ticket type. An event offering "Early Bird", "General Admission", and "VIP" pricing has three ticket types.',
              ),
};

export const FREE_OPTION = {
    question: gettext('Is Eventbrite free to use if my events are free? '),
    answer: ({ launchNewPackageAndPricing }) =>
        launchNewPackageAndPricing
            ? gettext(
                  "Yes! It's free to use Eventbrite to post your event. We never charge fees for tickets to free events. If you have paid ticket options, fees are paid by your attendees by default.",
              )
            : gettext(
                  'Yes! Both our Essentials and Professional products are free for free tickets — we only charge you each time you sell a paid ticket. Organizers with only free events have access to everything in Essentials, and everything in Professional except for phone, chat, or email support.',
              ),
};

export const FREE_OPTION_AU = {
    question: gettext('Is Eventbrite free to use if my events are free? '),
    answer: ({ launchNewPackageAndPricing }) =>
        launchNewPackageAndPricing
            ? gettext(
                  "Yes! It's free to use Eventbrite to post your event. We never charge fees for tickets to free events. If you have paid ticket options, fees are paid by your attendees by default.",
              )
            : gettext(
                  'Yes! Both our Essentials and Professional solutions are free for free tickets — we only charge you each time you sell a paid ticket. All packages come with the ability to pass online ticket fees to attendees. Organisers who exclusively host free events have access to everything in the Essentials and Professional solutions with the exception of phone, chat, or email support. Creators of free events can still find support at our <a href="/support">Online Help Centre.</a>',
              ),
};

export const FREE_OPTION_NEW_TLDS = {
    question: gettext('Does Eventbrite have a free option?'),
    answer: ({ launchNewPackageAndPricing }) =>
        launchNewPackageAndPricing
            ? gettext(
                  "Yes! It's free to use Eventbrite to post your event. We never charge fees for tickets to free events. If you have paid ticket options, fees are paid by your attendees by default.",
              )
            : gettext(
                  'Yes! Both our Essentials and Professional products are free for free tickets — we only charge you each time you sell a paid ticket. Organisers with only free events have access to everything in Essentials and Professional.',
              ),
};

export const TICKETING_ESSENTIALS = {
    question: gettext('What are ticketing and registration "essentials"?'),
    answer: gettext(
        'Ticketing and registration essentials include the things you need to succesfully execute a simple event, including a mobile-optimized event page, general admission ticketing, a summary reporting dashboard, exportable attendee data, and on-site check-in.',
    ),
    cta: gettext(
        '<a href="/organizer/compare-packages">You can see a full list of features here.</a>',
    ),
};

export const TICKETING_ESSENTIALS_AU = {
    question: gettext('What are ticketing and registration "essentials"?'),
    answer: gettext(
        'Ticketing and registration essentials are the tools you need to successfully administer a simple event, including general admission ticketing with secure checkout, a mobile-optimised event page, email invitations, a summary reporting dashboard, exportable attendee data, and on-site check-in.',
    ),
    cta: gettext(
        '<a href="/organizer/compare-packages">Check out the full list of features here.</a>',
    ),
};

export const PACKAGE_DIFFER = {
    question: gettext('Do packages and prices differ by location or currency?'),
    answer: ({
        pricing,
        hasPriceTax = false,
        showLowAtvUi = false,
        launchNewPackageAndPricing,
    }) => {
        let defaultText = gettext(
            "Eventbrite's pricing is based on your package. Packages differ slightly based on feature availability for each region, and price tiers determined by the event's currency. Based on your current location, Essentials pricing is %(package1)s per paid ticket and Professional pricing is %(package2)s per paid ticket. Both packages are always free for free tickets.",
            pricing,
        );

        if (hasPriceTax) {
            defaultText = gettext(
                "Eventbrite's pricing is based on your package. Packages differ slightly based on feature availability for each region, and price tiers determined by the event's currency. Both packages are always free for free tickets.",
            );
        }

        // EXPERIMENT_MARKER=LOW_ATV_CAPPED_FEE
        if (showLowAtvUi) {
            defaultText = gettext(
                'Eventbrite determines prices based on your package. There is a small difference between the packages, depending on the availability of functions in each region, and price levels are determined by the currency of the event. Based on your current location, the Essentials package price is %(package1)s per ticket paid, and for the Professional package the price is %(package2)s per ticket paid. Both packages are always free for free tickets. Now through August 13th, we are offering limited time pricing for tickets %(package1Threshold)s and under. During this promotion, tickets priced %(package1Threshold)s and under in the Essentials package will have a flat fee of %(package1CappedFee)s per paid ticket, while tickets %(package2Threshold)s and under in the Professional package will have a flat fee of %(package2CappedFee)s per paid ticket.',
                pricing,
            );
        }

        if (launchNewPackageAndPricing) {
            defaultText = gettext(
                'Yes! Eventbrite\'s pricing* is based on your event location & currency of event. However, free tickets and events are free. <a href="/support/articleredirect?anum=7285">Click here to see packages and pricing for every supported currency.</a> <br />  <i>* Additional payment processing fees or tax may be applicable.</i>',
            );
        }

        return defaultText;
    },
    cta: gettext(
        '<a href="/support/articleredirect?anum=7285">Click here to see packages and pricing for every supported currency.</a>',
    ),
};

export const PACKAGE_DIFFER_AU = {
    question: gettext('Do packages and prices differ by location or currency?'),
    answer: ({
        pricing,
        hasPriceTax = false,
        showLowAtvUi = false,
        launchNewPackageAndPricing,
    }) => {
        let defaultText = gettext(
            'Eventbrite\'s pricing is based on your selected package. Packages differ slightly based on feature availability for each region, and price tiers determined by the event\'s currency. If your current location is in Australia, <a href="/organizer/essentials">Essentials pricing</a> is %(package1)s per paid ticket and <a href="/organizer/professional">Professional pricing</a> is %(package2)s per paid ticket. Both packages are always free for free tickets.',
            pricing,
        );

        if (hasPriceTax) {
            defaultText = gettext(
                "Eventbrite's pricing is based on your package. Packages differ slightly based on feature availability for each region, and price tiers determined by the event's currency. Both packages are always free for free tickets.",
            );
        }

        // EXPERIMENT_MARKER=LOW_ATV_CAPPED_FEE
        if (showLowAtvUi) {
            defaultText = gettext(
                'Eventbrite determines prices based on your package. There is a small difference between the packages, depending on the availability of functions in each region, and price levels are determined by the currency of the event. Based on your current location, the Essentials package price is %(package1)s per ticket paid, and for the Professional package the price is %(package2)s per ticket paid. Both packages are always free for free tickets. Now through August 13th, we are offering limited time pricing for tickets %(package1Threshold)s and under. During this promotion, tickets priced %(package1Threshold)s and under in the Essentials package will have a flat fee of %(package1CappedFee)s per paid ticket, while tickets %(package2Threshold)s and under in the Professional package will have a flat fee of %(package2CappedFee)s per paid ticket.',
                pricing,
            );
        }

        if (launchNewPackageAndPricing) {
            defaultText = gettext(
                'Yes! Eventbrite\'s pricing* is based on your event location & currency of event. However, free tickets and events are free. <a href="/support/articleredirect?anum=7285">Click here to see packages and pricing for every supported currency.</a> <br />  <i>* Additional payment processing fees or tax may be applicable.</i>',
            );
        }

        return defaultText;
    },
    cta: gettext(
        '<a href="/support/articleredirect?anum=7285">See packages and pricing for every supported currency here.</a>',
    ),
};

export const PRIVATE_EVENT = {
    question: gettext(
        "What if I don't want to list my event on Eventbrite and partner sites?",
    ),
    answer: ({ launchNewPackageAndPricing }) =>
        launchNewPackageAndPricing
            ? gettext(
                  'When you create a new event, you\'ll have the opportunity to set your event as "Public" or "Private." If you choose "Private", it won\'t be listed on Eventbrite or any of our partner sites. If you choose "Public", the event will be listed on both Eventbrite and its partner sites to help you reach new audiences.',
              )
            : gettext(
                  'Each time you create a ticketing or registration page, you\'ll have the opportunity to mark your event as "Public" or "Private." If you mark it "Private", it won\'t be listed on Eventbrite or any of our partner sites. If you mark it "Public", it\'ll be listed on both to help you reach new audiences.',
              ),
};

export const PRIVATE_EVENT_AU = {
    question: gettext(
        "What if I don't want to list my event on Eventbrite and partner sites?",
    ),
    answer: ({ launchNewPackageAndPricing }) =>
        launchNewPackageAndPricing
            ? gettext(
                  'When you create a new event, you\'ll have the opportunity to set your event as "Public" or "Private." If you choose "Private", it won\'t be listed on Eventbrite or any of our partner sites. If you choose "Public", the event will be listed on both Eventbrite and its partner sites to help you reach new audiences.',
              )
            : gettext(
                  'Whenever you create an event, you\'ll have the opportunity to determine if you want your ticketing or registration page to be "Public" or "Private." If you mark it "Private", it won\'t be listed on Eventbrite or any of our partner sites. If you mark it "Public", it\'ll be listed on both to help you reach new audiences.',
              ),
};

export const PAY_FEES = {
    question: gettext('How do I pay my fees?'),
    answer: ({ launchNewPackageAndPricing }) =>
        launchNewPackageAndPricing
            ? gettext(
                  'Most organizers pass fees to their attendees, and pay nothing out of pocket to use Eventbrite! By default, when using <a href="/support/articleredirect?anum=000004373">Eventbrite Payment Processing</a>, your attendees will automatically pay fees but you have the option to "absorb" your fees and pay them out of your ticketing revenue, if you\'d rather.',
              )
            : gettext(
                  'When using Eventbrite\'s payment processing, you have the option to either "absorb" your fees and pay them out of your ticketing revenue, or pass them along to your ticket buyers. The majority of organizers who pass fees to their buyers pay nothing out of pocket to use Eventbrite! If you choose to use a third party payment processor, you\'ll be invoiced for your fees.',
              ),
};

export const PAY_FEES_AU = {
    question: gettext('How do I pay my fees?'),
    answer: ({ launchNewPackageAndPricing }) =>
        launchNewPackageAndPricing
            ? gettext(
                  'Most organizers pass fees to their attendees, and pay nothing out of pocket to use Eventbrite! By default, when using <a href="/support/articleredirect?anum=000004373">Eventbrite Payment Processing</a>, your attendees will automatically pay fees but you have the option to "absorb" your fees and pay them out of your ticketing revenue, if you\'d rather.',
              )
            : gettext(
                  'When using Eventbrite\'s payment processing, you have the option to either "absorb" your fees and pay them out of your ticketing revenue, or pass them along to your ticket buyers. The majority of organisers who pass fees to their buyers pay nothing out of pocket to use Eventbrite! If you choose to use a third party payment processor (such as PayPal), you\'ll be <a href="/support/articles/en_AU/Troubleshooting/understanding-eventbrite-fee-invoices?lg=en_AU">invoiced</a> for your fees.',
              ),
};

export const PAY_FEES_NEW_TLDS = {
    question: gettext('How do I pay my fees?'),
    answer: ({ launchNewPackageAndPricing }) =>
        launchNewPackageAndPricing
            ? gettext(
                  'Most organizers pass fees to their attendees, and pay nothing out of pocket to use Eventbrite! By default, when using <a href="/support/articles/Troubleshooting/getting-started-with-eventbrite-payment-processing?">Eventbrite Payment Processing</a>, your attendees will automatically pay fees but you have the option to "absorb" your fees and pay them out of your ticketing revenue, if you\'d rather.',
              )
            : gettext(
                  'Using PayPal you\'ll be invoiced for your fees. We are working hard to bring you Eventbrite Payment Processing soon as an alternative to PayPal. When using Eventbrite\'s payment processing, you have the option to either "absorb" your fees and pay them out of your ticketing revenue, or pass them along to your ticket buyers. The majority of organisers who pass fees to their buyers pay nothing out of pocket to use Eventbrite!',
              ),
};

export const PAYMENT_OPTIONS = {
    question: gettext('What are my payment processing options?'),
    answer: gettext(
        "The vast majority of Eventbrite organizers use Eventbrite Payment Processing, our native payment processor that enables full access to all features in your selected package and has historically increased sales conversion by up to 20%. If you already have a PayPal or Auth.net payment processing account, you're welcome to use either of those providers as well.",
    ),
};

export const PAYMENT_OPTIONS_AU = {
    question: gettext('What are my payment processing options?'),
    answer: gettext(
        'Most Eventbrite organisers use <a href="/support/articles/en_AU/Troubleshooting/getting-started-with-eventbrite-payment-processing?lg=en_AU">Eventbrite Payment Processing</a>, our native payment processor that enables full access to all the features in your selected package. Eventbrite Payment Processing is the default option, and in most cases is the most cost efficient and smoothest experience. Using Eventbrite Payment Processing has historically increased sales conversion by up to 20%. If you already have a PayPal payment processing account, you\'re welcome to use this provider as well. Keep in mind that PayPal charges you their own payment processing fee.',
    ),
};

export const PAYMENT_OPTIONS_NEW_TLDS = {
    question: gettext('What are my payment processing options?'),
    answer: gettext(
        'If you already have a PayPal payment processing account you can use that. Eventbrite Payment Processing is our native payment processor that enables access to additional features in your selected package and has historically increased sales conversion by up to 20%. We are working hard to bring you this solution soon.',
    ),
};
